<script setup lang="ts">
import { ref } from 'vue';
import { errors } from '..';

import Dropdown, { type DropdownItems } from '@/components/Dropdown.vue';
import BirthDate from '@/components/input/BirthDate.vue';
import Country from '@/components/input/Country.vue';

import type { Book } from '@/types/book';
import type { BookingDetails } from '@/types/form';

const form = defineModel<BookingDetails>('form', { required: true });

const props = defineProps<{ booking: Book }>();

defineEmits<{ (e: 'submit:form', value: BookingDetails): void }>();

const titleOptions = ref<DropdownItems[]>([
  { name: 'Dhr.', value: 'Dhr' },
  { name: 'Mevr.', value: 'Mevr' },
]);

const { adults, children } = props.booking.pricing.persons;

function addPassenger() {
  form.value.passengers.push({
    title: 'Dhr',
    first_name: '',
    last_name: '',
    date_of_birth: '',
  });
}

function initializePassengers() {
  const totalRequiredPassengers = parseInt(adults) + parseInt(children);
  while (form.value.passengers.length + 1 < totalRequiredPassengers) addPassenger();
}

initializePassengers();
</script>

<template>
  <div class="block block--alert margin-big">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.5 19.5">
      <path
        fill="#CA2D31"
        d="M9.75 0c5.385 0 9.75 4.365 9.75 9.75s-4.365 9.75-9.75 9.75S0 15.135 0 9.75 4.365 0 9.75 0Zm0 1.5a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5Zm.159 6.75a.75.75 0 0 1 .75.75l-.001 3.5h.402a.75.75 0 1 1 0 1.5H8.75a.75.75 0 1 1 0-1.5h.408V9.75h-.4a.75.75 0 0 1-.742-.648L8.009 9a.75.75 0 0 1 .75-.75h1.15ZM9.6 4.997a1 1 0 0 1 1 .968v.032a1 1 0 1 1-1-1Z"
      />
    </svg>
    <div class="block__content">
      <p>
        Voor het boeken van de vluchten is het belangrijk dat de namen van de passagiers exact overeenkomen met de namen in het paspoort.
        Noteer dus de namen zoals in het paspoort in onderstaande velden. Achteraf wijzigen kan extra kosten veroorzaken. Bij de voornaam
        volstaat alleen de eerste voornaam volgens paspoort
      </p>
    </div>
  </div>

  <div class="block margin">
    <h3 class="block__title">
      Passagier 1
      <span>(hoofdboeker)</span>
    </h3>
  </div>

  <div class="block block--form">
    <div class="block__input name">
      <label for="first_name"> Voornaam </label>
      <div class="input-item">
        <Dropdown input-class="title-class" v-model:value="form.title" :options="titleOptions" placeholder="Aanhef." />
        <span class="error" v-if="errors.has('title')">{{ errors.get('title')![0] }}</span>
      </div>
      <div class="input-item">
        <input type="text" id="first_name" v-model="form.first_name" />
        <span class="error" v-if="errors.has('first_name')">{{ errors.get('first_name')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="last_name"> Achternaam </label>
      <div class="input-item">
        <input type="text" id="last_name" v-model="form.last_name" />
        <span class="error" v-if="errors.has('last_name')">{{ errors.get('last_name')![0] }}</span>
      </div>
    </div>
    <div class="block__input date">
      <label for="date_of_birth"> Geboortedatum </label>
      <div class="input-item">
        <BirthDate v-model:dob="form.date_of_birth" id="date_of_birth" />
        <span class="error" v-if="errors.has('date_of_birth')">{{ errors.get('date_of_birth')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="address"> Adres </label>
      <div class="input-item">
        <input type="text" id="address" v-model="form.address" />
        <span class="error" v-if="errors.has('address')">{{ errors.get('address')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="zipcode"> Postcode </label>
      <div class="input-item">
        <input type="text" id="zipcode" v-model="form.zipcode" />
        <span class="error" v-if="errors.has('zipcode')">{{ errors.get('zipcode')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="place"> Plaats </label>
      <div class="input-item">
        <input type="text" id="place" v-model="form.place" />
        <span class="error" v-if="errors.has('place')">{{ errors.get('place')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="country"> Land </label>
      <div class="input-item">
        <Country v-model:value="form.country" />
        <span class="error" v-if="errors.has('country')">{{ errors.get('country')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="phone"> Telefoonnummer </label>
      <div class="input-item">
        <input type="tel" id="phone" v-model="form.phone" />
        <span class="error" v-if="errors.has('phone')">{{ errors.get('phone')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="email"> E-mailadres </label>
      <div class="input-item">
        <input type="email" id="email" v-model="form.email" />
        <span class="error" v-if="errors.has('email')">{{ errors.get('email')![0] }}</span>
      </div>
    </div>
    <div class="block__input">
      <label for="email_repeat"> E-mailadres (bevestiging) </label>
      <div class="input-item">
        <input type="email" id="email_repeat" v-model="form.email_repeat" />
        <span class="error" v-if="errors.has('email_repeat')">{{ errors.get('email_repeat')![0] }}</span>
      </div>
    </div>
  </div>
  <hr />
  <template v-for="(passenger, index) in form.passengers" :key="index">
    <div class="block margin">
      <h3 class="block__title">Passagier {{ index + 2 }}</h3>
    </div>
    <div class="block block--form">
      <div class="block__input name">
        <label :for="`passengers.${index}.first_name`"> Voornaam </label>
        <div class="input-item">
          <Dropdown input-class="title-class" v-model:value="form.passengers[index].title" :options="titleOptions" placeholder="Aanhef." />
          <span class="error" v-if="errors.has(`passengers.${index}.title`)">
            {{ errors.get(`passengers.${index}.first_name`)![0] }}
          </span>
        </div>
        <div class="input-item">
          <input type="text" id="first_name" v-model="form.passengers[index].first_name" />
          <span class="error" v-if="errors.has(`passengers.${index}.first_name`)">
            {{ errors.get(`passengers.${index}.first_name`)![0] }}
          </span>
        </div>
      </div>
      <div class="block__input">
        <label :for="`passengers.${index}.last_name`"> Achternaam </label>
        <div class="input-item">
          <input type="text" :id="`passengers.${index}.last_name`" v-model="passenger.last_name" />
          <span class="error" v-if="errors.has(`passengers.${index}.last_name`)">
            {{ errors.get(`passengers.${index}.last_name`)![0] }}
          </span>
        </div>
      </div>
      <div class="block__input date">
        <label :for="`passengers.${index}.date_of_birth`">Geboortedatum:</label>
        <div class="input-item">
          <BirthDate v-model:dob="passenger.date_of_birth" />
          <span class="error" v-if="errors.has(`passengers.${index}.date_of_birth`)">
            {{ errors.get(`passengers.${index}.date_of_birth`)![0] }}
          </span>
        </div>
      </div>
    </div>
  </template>
  <hr />
  <div class="block block--form">
    <div class="block__input">
      <label for="notes">Opmerkingen</label>
      <div class="input-item">
        <textarea v-model="form.notes" cols="30" rows="8"></textarea>
      </div>
    </div>
  </div>
  <hr />
  <div class="block block--boxed">
    <p>
      De prijs is inclusief SGR bijdrage (à €5 p.p.) en Calamiteitenfonds (à €2,50 per boeking). Alle acties, aanbiedingen en kortingen zijn
      verwerkt in de totaalprijs.
    </p>
    <p>
      Na het maken van de boeking vragen we je direct een aanbetaling van €300 per persoon te doen. Door te klikken op 'Direct boeken' wordt
      de boeking definitief vastgelegd en ga je een betalingsverplichting aan. Je kunt de reis dan niet meer zonder kosten annuleren. Als je
      de reis binnen 12 uur annuleert, zijn de annuleringskosten gelijk aan de aanbetaling. Als je later annuleert, zijn de
      annuleringskosten zoals beschreven op deze pagina. Omdat je bij ons kunt boeken met een laag aanbetalingsbedrag, kan het voorkomen dat
      je moet bijbetalen als je annuleert.
    </p>
  </div>
  <div class="block block--form margin">
    <div class="block__check">
      <input type="checkbox" id="terms" name="terms" v-model="form.terms" />
      <label for="terms">
        <p>
          Ik ga akkoord met de <a href="/reisvoorwaarden/">reisvoorwaarden</a> van Time Out Tours BV (lid van de SGR (Stichting Garantie
          Reisgelden) onder nummer 3251) en ga akkoord dat mijn persoonsgegevens worden verwerkt zoals beschreven in het
          <a href="/privacybeleid/">privacybeleid</a>.
        </p>
      </label>
      <span class="error" v-if="errors.has(`terms`)">
        {{ errors.get(`terms`)![0] }}
      </span>
    </div>
  </div>
</template>

<style>
.title-class {
  border: 1px solid var(--gray-alt);
  /* line-height: 150%; */
}

.input-item {
  display: grid;
  gap: 4px;
}

.error {
  color: red;
  font-size: 12px;
}
</style>
